/**
 * CurriculumMap.jsx
 *
 * Main container for the long-term curriculum planning view.
 * Displays a 2D grid with semesters as rows and requirement categories as columns,
 * showing completed, enrolled, and planned courses in their appropriate cells.
 * Features nested/hierarchical category headers matching university curriculum structure.
 *
 * Drag-and-drop: DndContext is provided at the parent level (Biddit2.jsx) to enable
 * cross-component dragging from EventListContainer to CurriculumMap.
 */

import { useRecoilValue } from "recoil";
import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { DownloadIcon, ArrowLeftIcon } from "@heroicons/react/solid";
import { EyeIcon, EyeOffIcon, SwitchHorizontalIcon } from "@heroicons/react/outline";
import { curriculumMapSelector } from "../../recoil/curriculumMapSelector";
import { authTokenState } from "../../recoil/authAtom";
import { curriculumPlansRegistryState } from "../../recoil/curriculumPlansRegistryAtom";
import { useScorecardFetching } from "../../helpers/useScorecardFetching";
import { useInitializeScoreCards } from "../../helpers/useInitializeScorecards";
import { useCurriculumMapCourseLoader } from "../../helpers/useCurriculumMapCourseLoader";
import usePlanManager from "../../helpers/usePlanManager";
import { useErrorHandler } from "../../errorHandling/useErrorHandler";
import { useDegradedMode } from "../../common/useDegradedMode";
import DegradedPlaceholder from "../../common/DegradedPlaceholder";
import LoadingText from "../../common/LoadingText";
import CurriculumGrid from "./CurriculumGrid";
import ProgramHeader from "./ProgramHeader";
import PlanSwitcher from "./PlanSwitcher";
import PlaceholderCreator from "./PlaceholderCreator";
import CategoryLegend from "./CategoryLegend";
import CurriculumMapTutorial, {
  TUTORIAL_STORAGE_KEY,
} from "./CurriculumMapTutorial";
import { CurriculumPlanProvider } from "./CurriculumPlanContext";

const DRAG_HINT_STORAGE_KEY = "biddit-curriculum-drag-hint-dismissed";
const GRADES_HIDDEN_STORAGE_KEY = "biddit-curriculum-grades-hidden";
const AXIS_FLIPPED_STORAGE_KEY = "biddit-curriculum-axis-flipped";

/**
 * Animated hint showing users they can drag courses from the left panel
 */
const DragHint = ({ onDismiss }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [canDismiss, setCanDismiss] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setCanDismiss(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  if (!isVisible) return null;

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg shadow-sm">
      {/* Animated arrow pointing left */}
      <div className="flex items-center animate-bounce-horizontal">
        <ArrowLeftIcon className="w-5 h-5 text-blue-500" />
      </div>

      <div className="flex-1 text-sm text-blue-700">
        <span className="font-medium">Drag courses</span>
        <span className="text-blue-600">
          {" "}
          from the Course List on the left into the grid. To get all your previously saved courses into the Curriculum map, click {" "}
        </span>
        <span className="font-medium">&quot;Import Selected Courses&quot;</span>
        <span className="text-blue-600">
          .
        </span>
      </div>

      {canDismiss && (
        <button
          onClick={() => {
            setIsVisible(false);
            localStorage.setItem(DRAG_HINT_STORAGE_KEY, "true");
            onDismiss?.();
          }}
          className="text-blue-400 hover:text-blue-600 text-xs font-medium px-2 py-1 rounded hover:bg-blue-100 transition-colors"
        >
          Got it
        </button>
      )}
    </div>
  );
};

DragHint.propTypes = {
  onDismiss: PropTypes.func,
};

const CurriculumMap = () => {
  const curriculumData = useRecoilValue(curriculumMapSelector);
  const authToken = useRecoilValue(authTokenState);
  const plansRegistry = useRecoilValue(curriculumPlansRegistryState);
  const { loadPlans, importSelectedCourses } = usePlanManager();
  const scorecardFetching = useScorecardFetching();
  const handleError = useErrorHandler();
  const { isDegradedMode } = useDegradedMode();

  // Lazily load course catalogs for every semester the map shows (e.g. past
  // semesters) so course credits resolve to their real ECTS instead of "?".
  const mapSemesterKeys = useMemo(
    () => (curriculumData.semesters || []).map((s) => s.key),
    [curriculumData.semesters],
  );
  useCurriculumMapCourseLoader(authToken, mapSemesterKeys);
  const [fetchAttempted, setFetchAttempted] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showDragHint, setShowDragHint] = useState(
    () => localStorage.getItem(DRAG_HINT_STORAGE_KEY) !== "true",
  );
  const [tutorialOpen, setTutorialOpen] = useState(
    () => localStorage.getItem(TUTORIAL_STORAGE_KEY) !== "true",
  );
  const [gradesHidden, setGradesHidden] = useState(
    () => localStorage.getItem(GRADES_HIDDEN_STORAGE_KEY) === "true",
  );
  const [isAxisFlipped, setIsAxisFlipped] = useState(() => {
    // Default to flipped (semesters on X, course types on Y) for new users;
    // respect an explicit saved preference either way.
    const stored = localStorage.getItem(AXIS_FLIPPED_STORAGE_KEY);
    return stored === null ? true : stored === "true";
  });
  // Click-to-place is disabled; placementMode stays null. Grid still receives
  // it so re-enabling later only requires restoring the useState + handlers.
  const placementMode = null;

  // Handle import button click
  const handleImportCourses = async () => {
    setIsImporting(true);
    try {
      await importSelectedCourses();
    } finally {
      setIsImporting(false);
    }
  };

  // Initialize scorecard data
  useInitializeScoreCards(handleError);

  // Load curriculum plans from API on mount (if not already loaded).
  // isDegradedMode is in the deps (and loadPlans itself is guarded against
  // degraded mode) so that when the kill switch clears, this effect
  // re-fires and re-triggers the load instead of leaving plans empty.
  useEffect(() => {
    if (!plansRegistry.isLoaded && authToken && !isDegradedMode) {
      loadPlans();
    }
  }, [plansRegistry.isLoaded, authToken, loadPlans, isDegradedMode]);

  // Auto-fetch if needed
  useEffect(() => {
    const fetchIfNeeded = async () => {
      if (!curriculumData.isLoaded && !fetchAttempted && authToken) {
        setFetchAttempted(true);
        try {
          await scorecardFetching.fetchAll(authToken);
        } catch (error) {
          console.error("[CurriculumMap] Error fetching scorecard:", error);
        }
      }
    };
    fetchIfNeeded();
  }, [curriculumData.isLoaded, fetchAttempted, authToken, scorecardFetching]);

  // Curriculum plans are SHSG-backed; bail out before the dnd-kit grid mounts
  // so the pane doesn't hang on a perpetual loading spinner while degraded.
  if (isDegradedMode) {
    return (
      <div className="flex flex-col h-full px-6 py-4">
        <h1 className="text-2xl font-bold mb-4 text-gray-900">
          Curriculum Map
        </h1>
        <DegradedPlaceholder feature="Curriculum Map" />
      </div>
    );
  }

  // This panel stays mounted across tab switches, so `fetchAttempted` now lives
  // for the whole session — a failed first attempt is no longer cleared by a
  // remount. Without an explicit retry the map would sit on its skeleton until
  // a full page reload, so surface the failure and let the user re-arm it.
  const loadFailed =
    fetchAttempted &&
    !curriculumData.isLoaded &&
    !scorecardFetching.loading &&
    Boolean(scorecardFetching.error);

  if (loadFailed) {
    return (
      <div className="flex flex-col h-full px-6 py-4">
        <h1 className="text-2xl font-bold mb-4 text-gray-900">
          Curriculum Map
        </h1>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center py-8 bg-white rounded-lg border border-gray-200 max-w-md p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Could not load your curriculum
            </h3>
            <p className="text-gray-600 mb-4">
              Your academic data didn&apos;t come back from the university.
            </p>
            <button
              onClick={() => setFetchAttempted(false)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-hsg-600 hover:bg-hsg-700 rounded-md transition-colors shadow-sm"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (!curriculumData.isLoaded || !plansRegistry.isLoaded) {
    return (
      <div className="flex flex-col h-full px-6 py-4">
        <h1 className="text-2xl font-bold mb-4 text-gray-900">
          Curriculum Map
        </h1>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-64 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-48 mx-auto mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-56 mx-auto"></div>
            </div>
            <LoadingText>
              {!plansRegistry.isLoaded
                ? "Loading your curriculum plans..."
                : "Loading curriculum data..."}
            </LoadingText>
          </div>
        </div>
      </div>
    );
  }

  // No program data
  if (!curriculumData.program) {
    return (
      <div className="flex flex-col h-full px-6 py-4">
        <h1 className="text-2xl font-bold mb-4 text-gray-900">
          Curriculum Map
        </h1>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center py-8 bg-gradient-to-br from-hsg-50 to-blue-50 rounded-lg border border-hsg-200 max-w-md p-6">
            <div className="text-5xl mb-4">📋</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No Academic Data Found
            </h3>
            <p className="text-gray-600">
              We couldn&apos;t find a study program on your transcript. If
              you&apos;ve only just enrolled, your scorecard may not be
              published yet.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CurriculumPlanProvider>
      <div className="flex flex-col h-full overflow-hidden">
        <CurriculumMapTutorial
          isOpen={tutorialOpen}
          onDismiss={() => {
            localStorage.setItem(TUTORIAL_STORAGE_KEY, "true");
            setTutorialOpen(false);
          }}
        />
        {/* Header with program info and progress */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-white">
          <ProgramHeader
            program={curriculumData.program}
            onHelpClick={() => setTutorialOpen(true)}
          />
          <div className="flex items-center gap-4 flex-wrap">
            <PlanSwitcher />
            <button
              onClick={handleImportCourses}
              disabled={isImporting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-hsg-600 hover:bg-hsg-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-md transition-colors shadow-sm"
              title="Import selected courses from your study plan into this curriculum plan"
            >
              <DownloadIcon className="w-4 h-4" />
              {isImporting ? "Importing..." : "Import Selected Courses"}
            </button>
            <button
              onClick={() => {
                setGradesHidden((prev) => {
                  const next = !prev;
                  localStorage.setItem(GRADES_HIDDEN_STORAGE_KEY, String(next));
                  return next;
                });
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              title={gradesHidden ? "Show grades" : "Hide grades"}
            >
              {gradesHidden ? (
                <EyeOffIcon className="w-4 h-4" />
              ) : (
                <EyeIcon className="w-4 h-4" />
              )}
              {gradesHidden ? "Show Grades" : "Hide Grades"}
            </button>
            <button
              onClick={() => {
                setIsAxisFlipped((prev) => {
                  const next = !prev;
                  localStorage.setItem(AXIS_FLIPPED_STORAGE_KEY, String(next));
                  return next;
                });
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              title="Flip axes — swap semesters and categories"
            >
              <SwitchHorizontalIcon className="w-4 h-4" />
              Flip Axes
            </button>
          </div>
          <PlaceholderCreator />
        </div>

        {/* Main content area: grid (courses are dragged from EventListContainer) */}
        <div className="flex-1 overflow-auto scrollbar-thin-visible px-6 py-4">
          {/* Drag hint - shows animation to guide users */}
          {showDragHint && (
            <div className="mb-4">
              <DragHint onDismiss={() => setShowDragHint(false)} />
            </div>
          )}

          <CurriculumGrid
            categories={curriculumData.flatCategories}
            categoryHierarchy={curriculumData.categoryHierarchy}
            semesters={curriculumData.semesters}
            coursesBySemesterAndCategory={
              curriculumData.coursesBySemesterAndCategory
            }
            validations={curriculumData.validations}
            placementMode={placementMode}
            gradesHidden={gradesHidden}
            isAxisFlipped={isAxisFlipped}
          />
        </div>

        {/* Legend at bottom */}
        <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50">
          <CategoryLegend />
        </div>
      </div>
    </CurriculumPlanProvider>
  );
};

export default CurriculumMap;
